import { TwoferAds } from '@generated/components/TwoferAds';
import { MediaKitShare } from '@generated/components/MediaKitShare';
import { PortalPrintAd } from '@generated/components/PortalPrintAd';
import { FreeClassifiedsAd } from '@generated/components/FreeClassifiedsAd';
import { InstaClassifieds } from '@generated/components/InstaClassifieds';
import { useRef, useState, type ReactNode } from 'react';
import { toPng, toJpeg } from 'html-to-image';
import { Download, Loader2 } from 'lucide-react';
import { AdEditsProvider, AdFrame } from '@generated/components/AdEdits';

const LOGO = 'https://files-public.monday.com/use1/8d2d6138-0d85-4478-bf31-1f68764cdf6d/LIGHT%20BLUE%20Red%20Hook%20Star-Revue%20logo%20square.png';
const PHOTO = 'https://files-public.monday.com/use1/1e825c5d-b48c-473b-b358-fd8a15aebbdb/Enrique.jpg';
const BULLETS = ['Advertise your business', 'Place a classified', 'Send us a news tip', 'Check out our merch', 'Write a letter to the editor', 'Discover our Direct Mail service'];
const BARS = ['bg-primary', 'bg-[hsl(var(--brand-yellow))]', 'bg-[hsl(var(--brand-blue))]', 'bg-[hsl(var(--brand-orange))]', 'bg-[hsl(var(--brand-green))]'];
const Bars = ({ h }: { h: string }) => <div className={`flex ${h}`}>{BARS.map((b) => <div key={b} className={`flex-1 ${b}`} />)}</div>;
const head = 'font-[family-name:var(--font-heading)]';
const bg = 'bg-[hsl(var(--paper))] text-foreground';

type Ad = { id: string; label: string; w: number; h: number; body: ReactNode };
const ADS: Ad[] = [
  { id: 'rectangle', label: 'Sidebar box · 300 × 250', w: 300, h: 250, body: (
    <div className={`flex h-full flex-col ${bg} border-2 border-foreground`}>
      <div className="flex flex-1 flex-col items-center justify-center px-4 text-center">
        <img src={LOGO} alt="" crossOrigin="anonymous" className="h-16 w-16" />
        <div className={`${head} mt-1 text-[22px] font-bold leading-tight text-primary`}>Advertise in the Star-Revue</div>
        <div className="mt-1 text-[12px] leading-snug">Place your ad online in minutes.<br />Free ad design included.</div>
        <div className="mt-3 bg-foreground px-4 py-1.5 text-[12px] font-bold uppercase tracking-wider text-background">Visit our portal →</div>
      </div><Bars h="h-2.5" />
    </div>) },
  { id: 'leaderboard', label: 'Wide banner · 728 × 90', w: 728, h: 90, body: (
    <div className={`flex h-full flex-col ${bg} border-2 border-foreground`}>
      <div className="flex flex-1 items-center gap-4 px-4">
        <img src={LOGO} alt="" crossOrigin="anonymous" className="h-14 w-14" />
        <div className="min-w-0 flex-1"><div className={`${head} text-[24px] font-bold leading-none text-primary`}>Advertise in the Star-Revue</div>
          <div className="mt-1 text-[12px]">Display ads · Classifieds · Subscriptions · News tips · Direct mail. All online.</div></div>
        <div className="shrink-0 bg-foreground px-4 py-2 text-[13px] font-bold uppercase tracking-wider text-background">Visit our portal →</div>
      </div><Bars h="h-2" />
    </div>) },
  { id: 'tall', label: 'Tall sidebar · 300 × 600', w: 300, h: 600, body: (
    <div className={`flex h-full flex-col ${bg} border-2 border-foreground`}>
      <div className="flex flex-1 flex-col items-center px-5 pt-3 text-center">
        <div className="h-[110px] w-40 shrink-0 overflow-hidden"><img src={LOGO} alt="" crossOrigin="anonymous" className="-mt-[27px] h-40 w-40 max-w-none" /></div>
        <div className={`${head} text-[30px] font-bold uppercase leading-[1.05] text-primary`}>Introducing our new portal</div>
        <div className="mt-2 text-[14px] leading-snug">An all-in-one place to interact with your friendly neighborhood paper.</div>
        <ul className="mt-2 w-full space-y-0.5 text-left text-[13px] font-semibold leading-tight">{BULLETS.map((t, i) => (
          <li key={t} className="flex items-center gap-2"><span className={`h-3 w-3 shrink-0 ${BARS[i % 5]}`} />{t}</li>))}</ul>
        <img src={PHOTO} alt="" crossOrigin="anonymous" className="mx-auto mt-auto mb-3 h-auto w-[85%] border border-foreground" />
      </div><Bars h="h-3" />
    </div>) },
  { id: 'square', label: 'Instagram / Facebook post · 1080 × 1080', w: 540, h: 540, body: (
    <div className={`flex h-full flex-col ${bg} border-2 border-foreground`}>
      <div className="flex flex-1 flex-col items-center px-6 pt-3 text-center">
        <div className="h-[124px] w-[180px] shrink-0 overflow-hidden"><img src={LOGO} alt="" crossOrigin="anonymous" className="-mt-[30px] h-[180px] w-[180px] max-w-none" /></div>
        <div className={`${head} text-[40px] font-bold uppercase leading-[1.05] text-primary`}>Introducing our new portal</div>
        <div className="mt-1 text-[17px] leading-snug">An all-in-one place to interact with your friendly neighborhood paper.</div>
        <div className="mt-4 grid w-full flex-1 grid-cols-[1fr_250px] items-start gap-5 pb-4">
          <ul className="space-y-1.5 text-left text-[16px] font-semibold leading-tight">{BULLETS.map((t, i) => (
            <li key={t} className="flex items-center gap-2"><span className={`h-3.5 w-3.5 shrink-0 ${BARS[i % 5]}`} />{t}</li>))}</ul>
          <div><img src={PHOTO} alt="" crossOrigin="anonymous" className="h-auto w-full border border-foreground" />
            <div className="mt-2 text-[15px] font-bold uppercase tracking-wider">Link in bio</div></div>
        </div>
      </div><Bars h="h-3" />
    </div>) },
];

export function WebAds() {
  const refs = useRef<Record<string, HTMLDivElement | null>>({});
  const [busy, setBusy] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const save = async (ad: Ad, kind: 'png' | 'jpg') => {
    const node = refs.current[ad.id]; if (!node) return;
    setBusy(ad.id + kind); setError(null);
    try {
      const ratio = 2;
      const url = kind === 'png' ? await toPng(node, { pixelRatio: ratio, cacheBust: true }) : await toJpeg(node, { pixelRatio: ratio, quality: 0.95, cacheBust: true, backgroundColor: '#ffffff' });
      const a = document.createElement('a'); a.href = url; a.download = `star-revue-portal-ad-${ad.id}.${kind}`; a.click();
    } catch (e) { console.error(e); setError('That image could not be saved. Please try again.'); }
    finally { setBusy(null); }
  };
  return (
    <AdEditsProvider><section>
      <MediaKitShare />
      <PortalPrintAd paper="vsr" />
      <PortalPrintAd paper="rhsr" />
      <FreeClassifiedsAd />
      <TwoferAds />
      <h2 className={`${head} text-3xl font-semibold`}>Web Ads for the portal</h2>
      <p className="mt-1 max-w-2xl text-sm text-muted-foreground">Ready-made ads to run on star-revue.com or post on social media. Each saves at double resolution so it stays sharp. Link the ad to: <span className="break-all font-medium text-foreground">https://ad-portal-gbrook8344s-team-company.v.monday.app</span></p>
      {error && <p role="alert" className="mt-3 text-sm text-destructive">{error}</p>}
      <div className="mt-6 space-y-10">{ADS.map((ad) => (
        <div key={ad.id}>
          <div className="mb-2 flex flex-wrap items-center justify-between gap-2"><h3 className="text-lg font-medium">{ad.label}</h3>
            <div className="flex gap-2">{(['png', 'jpg'] as const).map((k) => (
              <button key={k} type="button" disabled={!!busy} onClick={() => void save(ad, k)} className="inline-flex min-h-11 items-center gap-2 border border-foreground px-4 text-sm font-semibold disabled:opacity-50">
                {busy === ad.id + k ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}Save {k.toUpperCase()}</button>))}</div></div>
          <AdFrame adId={`portal-${ad.id}`} label={ad.label} capture={(el) => { refs.current[ad.id] = el; }} className="shrink-0" style={{ width: ad.w, height: ad.h }}>{ad.body}</AdFrame>
        </div>))}</div>
      <InstaClassifieds />
    </section></AdEditsProvider>
  );
}
